import { AvailiableLocales, I18nWrapper, useIntl } from '@workday/i18n/nextgen';
import * as React from 'react';
import { TOGGLE_FRENCH_CANADIAN_LANGUAGE, TOGGLE_PSEUDO_LANGUAGE_OPTIONS } from 'config/toggles';
import { ReqExecutor } from 'modules/common/requests/ExecutorFactory';
import i18nConfig from '../../../../../i18n.config';
import AllEnglishJson from '../../../../public/public/static/i18n/all/en.json';
import { SessionContext } from '../context/SessionContext';

/**
 * Complete list of available language locale IDs based on toggle states.
 */
export const allowedLanguages: AvailiableLocales[] = [
  'en',
  'en_US',
  ...(TOGGLE_FRENCH_CANADIAN_LANGUAGE ? ['fr_CA' as AvailiableLocales] : []),
  // explicitly placing the testing "pseudo" languages behind toggles
  // to allow for easier language switching during development
  ...(TOGGLE_PSEUDO_LANGUAGE_OPTIONS ? ['en-XA' as AvailiableLocales, 'en-XB' as AvailiableLocales] : []),
];

/**
 * Checks for locale in {@link allowedLanguages} list. Return the locale if it's allowed, otherwise return false.
 * Useful for guarding against a locale being set if it's not allowed.
 * @param option
 * @returns option | false
 */
export const isAvailableLanguageOption: (option: AvailiableLocales) => AvailiableLocales | boolean = (
  option: AvailiableLocales
) => (allowedLanguages.includes(option) ? option : false);

/**
 * Full list of translated language options for the language selector.
 */
export const getDevsiteLanguageOptions = () => {
  const intl = useIntl();
  return [
    {
      id: 'en' as AvailiableLocales,
      label: intl.formatMessage({
        id: 'core.DevsiteI18nProvider.getDevsiteLanguageOptions:English',
        defaultMessage: 'English',
        description: 'English language option from the language selector dropdown menu.',
      }),
    },
    {
      id: 'en_US' as AvailiableLocales,
      label: intl.formatMessage({
        id: 'core.DevsiteI18nProvider.getDevsiteLanguageOptions:English.US',
        defaultMessage: 'English (US)',
        description: 'US English language option from the language selector dropdown menu.',
      }),
    },
    {
      id: 'fr_CA' as AvailiableLocales,
      label: intl.formatMessage({
        id: 'core.DevsiteI18nProvider.getDevsiteLanguageOptions:French.CA',
        defaultMessage: 'Français (Canada)',
        description: 'Canadian French language option from the language selector dropdown menu.',
      }),
    },
    {
      id: 'en-XA' as AvailiableLocales,
      label: 'Pseudo XA',
    },
    {
      id: 'en-XB' as AvailiableLocales,
      label: 'Pseudo XB',
    },
  ].filter((option) => allowedLanguages.includes(option.id));
};

const devsiteIntlLoader = async (locale: AvailiableLocales) => {
  /**
   * Resource bundle is the official name for those json files given by g11n team.
   * This provider is responsible for loading all bundles.
   * Bundle names correspond with name property for each module defined in i18n.config.js
   * */
  const resourceBundles: string[] = i18nConfig().map((conf: { name: string }) => conf.name);

  const json = await Promise.all(
    resourceBundles.map((resourceBundle) =>
      ReqExecutor.get(`/public/static/i18n/${resourceBundle}/${locale}.json`).then((res) => res.body)
    )
  );
  return json.reduce((acc, cur) => ({ ...acc, ...cur }), {});
};

const DevsiteI18nProvider: React.FC<React.PropsWithChildren> = (props) => {
  const sessionContext = React.useContext(SessionContext);

  return (
    <I18nWrapper
      targetLocalization={sessionContext?.userLocale || { language: 'en', locale: 'en' }}
      config={{ loader: devsiteIntlLoader, preloadedMessages: { en: AllEnglishJson }, defaultLocale: 'en' }}
      loadingFallback={null} // currently still causes a white screen as the language loads
    >
      {props.children}
    </I18nWrapper>
  );
};

export default DevsiteI18nProvider;
